/**
 * Comprehensive input validation and sanitization utilities
 * Provides secure validation for various input types
 */

// Input sanitization functions
export const sanitizeInput = {
  text: (value) => {
    if (value === null || value === undefined || value === "") return "";
    return String(value).trim().replace(/[<>]/g, "");
  },

  number: (value) => {
    if (value === null || value === undefined || value === "") return "";
    return String(value).replace(/[^0-9.]/g, "");
  },

  email: (value) => {
    if (!value) return "";
    return value.trim().toLowerCase();
  },

  phone: (value) => {
    if (!value) return "";
    return value.replace(/[^0-9+\-\s]/g, "");
  },

  alphanumeric: (value) => {
    if (!value) return "";
    return value.replace(/[^a-zA-Z0-9\s]/g, "");
  },

  html: (value) => {
    if (!value) return "";
    return value.replace(/<[^>]*>/g, "");
  },
};

// Input validation functions
export const validateInput = {
  required: (value) => {
    return (
      value !== null && value !== undefined && value.toString().trim() !== ""
    );
  },

  email: (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  },

  phone: (value) => {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    return phoneRegex.test(value);
  },

  number: (value) => {
    return !isNaN(value) && Number(value) > 0;
  },

  positiveNumber: (value) => {
    return !isNaN(value) && Number(value) >= 0;
  },

  date: (value) => {
    const date = new Date(value);
    return date.toString() !== "Invalid Date" && date <= new Date();
  },

  minLength: (value, min) => {
    return value && value.length >= min;
  },

  maxLength: (value, max) => {
    return value && value.length <= max;
  },

  alphanumeric: (value) => {
    const alphanumericRegex = /^[a-zA-Z0-9\s]+$/;
    return alphanumericRegex.test(value);
  },

  noSpecialChars: (value) => {
    const noSpecialRegex = /^[a-zA-Z0-9\s\-_]+$/;
    return noSpecialRegex.test(value);
  },
};

// Comprehensive validation function
export const validateField = (value, rules) => {
  const errors = [];

  for (const rule of rules) {
    switch (rule.type) {
      case "required":
        if (!validateInput.required(value)) {
          errors.push(rule.message || "This field is required");
        }
        break;

      case "email":
        if (value && !validateInput.email(value)) {
          errors.push(rule.message || "Please enter a valid email address");
        }
        break;

      case "phone":
        if (value && !validateInput.phone(value)) {
          errors.push(rule.message || "Please enter a valid phone number");
        }
        break;

      case "number":
        if (value && !validateInput.number(value)) {
          errors.push(rule.message || "Please enter a valid number");
        }
        break;

      case "positiveNumber":
        if (value && !validateInput.positiveNumber(value)) {
          errors.push(rule.message || "Please enter a positive number");
        }
        break;

      case "date":
        if (value && !validateInput.date(value)) {
          errors.push(rule.message || "Please enter a valid date");
        }
        break;

      case "minLength":
        if (value && !validateInput.minLength(value, rule.value)) {
          errors.push(
            rule.message || `Minimum length is ${rule.value} characters`
          );
        }
        break;

      case "maxLength":
        if (value && !validateInput.maxLength(value, rule.value)) {
          errors.push(
            rule.message || `Maximum length is ${rule.value} characters`
          );
        }
        break;

      case "alphanumeric":
        if (value && !validateInput.alphanumeric(value)) {
          errors.push(
            rule.message || "Only letters, numbers, and spaces are allowed"
          );
        }
        break;

      case "noSpecialChars":
        if (value && !validateInput.noSpecialChars(value)) {
          errors.push(rule.message || "Special characters are not allowed");
        }
        break;

      case "custom":
        if (value && !rule.validator(value)) {
          errors.push(rule.message || "Invalid input");
        }
        break;
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Form validation helper
export const validateForm = (formData, validationRules) => {
  const errors = {};
  let isValid = true;

  for (const field in validationRules) {
    const fieldValue = formData[field];
    const fieldRules = validationRules[field];

    const validation = validateField(fieldValue, fieldRules);

    if (!validation.isValid) {
      errors[field] = validation.errors;
      isValid = false;
    }
  }

  return {
    isValid,
    errors,
  };
};

// Common validation rules
export const commonRules = {
  required: { type: "required", message: "This field is required" },
  email: { type: "email", message: "Please enter a valid email address" },
  phone: { type: "phone", message: "Please enter a valid phone number" },
  number: { type: "number", message: "Please enter a valid number" },
  positiveNumber: {
    type: "positiveNumber",
    message: "Please enter a positive number",
  },
  date: { type: "date", message: "Please enter a valid date" },
  alphanumeric: {
    type: "alphanumeric",
    message: "Only letters, numbers, and spaces are allowed",
  },
  noSpecialChars: {
    type: "noSpecialChars",
    message: "Special characters are not allowed",
  },
};

// Export default validation rules for common fields
export const defaultValidationRules = {
  name: [
    commonRules.required,
    commonRules.noSpecialChars,
    {
      type: "minLength",
      value: 2,
      message: "Name must be at least 2 characters",
    },
  ],
  email: [commonRules.required, commonRules.email],
  phone: [commonRules.required, commonRules.phone],
  quantity: [commonRules.required, commonRules.number],
  price: [commonRules.required, commonRules.positiveNumber],
  date: [commonRules.required, commonRules.date],
  description: [
    commonRules.required,
    {
      type: "minLength",
      value: 5,
      message: "Description must be at least 5 characters",
    },
  ],
  invoiceNumber: [
    commonRules.required,
    commonRules.alphanumeric,
    // {
    //   type: "minLength",
    //   // value: 3,
    //   message: "Invoice number must be at least 3 characters",
    // },
  ],
  truckNumber: [
    commonRules.required,
    commonRules.alphanumeric,
    {
      type: "minLength",
      value: 3,
      message: "Truck number must be at least 3 characters",
    },
  ],
};
